import React from 'react';
import {FormattedMessage} from 'react-intl';
import {useLocation} from 'react-router-dom';
import searchFilter from '../../../searchFilter';
import {ErrorBoundary} from 'react-error-boundary';
import Order from './order';
import OrderItem, {OrderItemDynamicQuantity, OrderItemRequestData} from './order-item';
import ErrorFallback from '../../../components/ErrorFallback';
import EffectRedirect from '../../../components/EffectRedirect';
import Coupon from './coupon';
import AuthorizationError from '../../../components/AuthorizationError';
import StemBalance from '../stem-balance';
import SuspenseFallback from './suspense-fallback';
import {ContentWrapperWithEmptyState} from './content-wrapper';
import FooterAction from './footer-action';
import ImpulseOrderWrapper from './order-wrapper';
import useP2pError from './use-p2p-error';
import useInvalidEndpoints from './use-invalid-endpoints';
import useCreateImpulse from './use-create-impulse';
import useCheckIfStem from './use-check-if-stem';
import ImpulseMessage from './message';
import useImpulseService from './use-impulse-service';
import useCoupon from './use-coupon';
import useLand from './use-land';
import OrderDetailsForm from './order-details-form';

const ImpulsePage = ({state, dispatch, subtitle, message, showOrderItemQuantity, dynamicEnpointQuantity}) => {
    const location = useLocation();
    const [newImpulseId, setNewImpulseId] = React.useState();
    const {
        isRefreshing,
        setIsRefreshing,
        requestPicks,
        currentLandPicks,
        serviceId,
        coupon,
        orderData,
        setLandMetas,
        landExists
    } = useLand({state});
    const {serviceById} = useImpulseService({serviceId});
    const {setCode} = useCoupon({state, setLandMetas});
    const {p2pError} = useP2pError({serviceId});
    const {invalidEndpoints, addInvalidEndpoint, resetInvalidEndpoints} = useInvalidEndpoints({serviceId});
    const {isStemImpulse} = useCheckIfStem({serviceId, state});
    const {
        createImpulse,
        isMutationInFlight,
        grecaptcha,
        register,
        formState,
        setValue
    } = useCreateImpulse({dispatch, currentLandPicks, coupon, serviceId, setNewImpulseId, resetInvalidEndpoints, invalidEndpoints});

    if (newImpulseId) {
        return <EffectRedirect to={`../payment${searchFilter(location.search, {impulseId: newImpulseId})}`} replace/>;
    }

    return (
        <div>
            <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                <React.Suspense fallback={<SuspenseFallback/>}>
                    {isStemImpulse && <StemBalance/>}
                    <ContentWrapperWithEmptyState {...{isRefreshing, landExists}}>
                            <ErrorBoundary {...{FallbackComponent: AuthorizationError}}>
                                <ImpulseOrderWrapper {...{subtitle}}>
                                    <Order {...{...orderData, coupon, isStemImpulse}}>
                                        {currentLandPicks.map((item, index) =>
                                            <OrderItemRequestData {...{key: index, pick: item, endpointId: item.endpointId, serviceById, quantityInCart: item.quantity, addInvalidEndpoint}}>
                                                {({endpointById}) => 
                                                    dynamicEnpointQuantity ?
                                                    <OrderItemDynamicQuantity {...{service: serviceById, ...endpointById, untie: item.untie, quantity: item.quantity, dispatch, stateKey: item.key}}/>
                                                    :
                                                    <OrderItem {...{service: serviceById, ...endpointById, untie: item.untie, quantity: endpointById.quantity < item.quantity ? endpointById.quantity : item.quantity, showOrderItemQuantity}}/>
                                                }
                                            </OrderItemRequestData>
                                        )}
                                        {invalidEndpoints.map((item, index) => 
                                            <OrderItem {...{key: index, service: serviceById, ...item.endpointData, untie: item.untie, quantity: item.quantity, maxQuantity: 0, showOrderItemQuantity, invalid: true}}/>
                                        )}
                                        <Coupon {...{coupon, setCode, isRefreshing, setIsRefreshing, picks: requestPicks}}/>
                                    </Order>
                                </ImpulseOrderWrapper>
                                <ImpulseMessage {...{message}}/>
                                <div className="padding-bottom-2rem mw768-padding-bottom-2dot5rem">
                                    <p className="text-2xl semibold color-gray-900 padding-top-2rem mw768-padding-top-2dot5rem">
                                        <FormattedMessage defaultMessage='Contact details'/>
                                    </p>
                                    <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                        <OrderDetailsForm {...{serviceId, registerFormField: register, formState, setValue}}/>
                                    </ErrorBoundary>
                                </div>
                                <FooterAction {...{createImpulse, isLoading: grecaptcha || isMutationInFlight || isRefreshing, actionDisabled: p2pError}}/>
                            </ErrorBoundary>
                    </ContentWrapperWithEmptyState>
                </React.Suspense>
            </ErrorBoundary>
        </div>
    )
};

export default React.memo(ImpulsePage);
